import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import ParticleCanvas from './ParticleCanvas';
import { ArrowDown, Users, Zap } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const DISCORD = 'https://discord.gg/yzM2p4sFb4';

function DiscordBadge() {
  const [members, setMembers] = useState(null);
  const [online, setOnline] = useState(null);
  useEffect(() => {
    axios.get(`${API}/discord/members`)
      .then(r => { setMembers(r.data.member_count); setOnline(r.data.online_count); })
      .catch(() => { setMembers(102); setOnline(12); });
  }, []);
  return (
    <motion.button
      data-testid="discord-badge"
      onClick={() => window.open(DISCORD, '_blank', 'noopener,noreferrer')}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.7 }}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.97 }}
      className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full border border-white/12 bg-white/[0.04] backdrop-blur-xl cursor-pointer hover:border-white/25 transition-all duration-300"
    >
      <span className="flex items-center gap-1.5">
        <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
        <span className="font-inter text-xs text-green-400 font-medium">{online ?? '—'} online</span>
      </span>
      <span className="w-px h-3 bg-white/15" />
      <span className="flex items-center gap-1.5">
        <Users size={10} className="text-zinc-500" />
        <span className="font-inter text-xs text-zinc-500">{members != null ? `${members}+ members` : '...'}</span>
      </span>
    </motion.button>
  );
}

export default function HeroSection() {
  const { t } = useLanguage();
  const scrollToShop = () => document.getElementById('shop')?.scrollIntoView({ behavior: 'smooth' });
  const scrollToReviews = () => document.getElementById('reviews')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <ParticleCanvas />

      {/* Focused radial glow */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: 'radial-gradient(ellipse 75% 55% at 50% 42%, rgba(255,255,255,0.065) 0%, transparent 65%)',
      }} />

      {/* Animated floating orbs */}
      <motion.div
        animate={{ scale: [1, 1.25, 1], x: [-30, 30, -30], opacity: [0.04, 0.09, 0.04] }}
        transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute top-1/4 left-1/4 w-[550px] h-[550px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)', filter: 'blur(70px)' }}
      />
      <motion.div
        animate={{ scale: [1, 1.3, 1], x: [20, -20, 20], opacity: [0.03, 0.07, 0.03] }}
        transition={{ duration: 11, repeat: Infinity, ease: 'easeInOut', delay: 3 }}
        className="absolute bottom-1/3 right-1/5 w-[400px] h-[400px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.06) 0%, transparent 70%)', filter: 'blur(80px)' }}
      />

      <div className="relative z-10 flex flex-col items-center text-center px-6 max-w-5xl mx-auto">

        {/* Discord Live Badge */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="mb-8">
          <DiscordBadge />
        </motion.div>

        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="mb-8"
        >
          <motion.span
            data-testid="hero-badge"
            animate={{ boxShadow: ['0 0 0px rgba(255,255,255,0)', '0 0 28px rgba(255,255,255,0.1)', '0 0 0px rgba(255,255,255,0)'] }}
            transition={{ duration: 3, repeat: Infinity }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/18 bg-white/[0.06] backdrop-blur-xl text-xs font-inter font-medium text-zinc-300 tracking-[0.2em] uppercase"
          >
            <Zap size={9} className="text-white" />
            {t.hero.badge}
          </motion.span>
        </motion.div>

        {/* VANTA — Premium gradient title */}
        <motion.h1
          data-testid="hero-title"
          initial={{ opacity: 0, y: 60, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
          className="font-outfit font-extralight tracking-[-0.03em] leading-none vanta-gradient-text mb-5"
          style={{
            fontSize: 'clamp(6rem, 18vw, 16rem)',
            textShadow: '0 0 120px rgba(255,255,255,0.12)',
            letterSpacing: '-0.02em',
          }}
        >
          VANTA
        </motion.h1>

        {/* Tagline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.38, ease: [0.16, 1, 0.3, 1] }}
          className="flex items-center gap-4 mb-8"
        >
          <div className="h-px w-12 bg-gradient-to-r from-transparent to-white/30" />
          <span className="font-outfit text-xs sm:text-sm tracking-[0.4em] font-light text-zinc-500 uppercase">
            {t.hero.title2}
          </span>
          <div className="h-px w-12 bg-gradient-to-l from-transparent to-white/30" />
        </motion.div>

        {/* Subtitle */}
        <motion.p
          data-testid="hero-subtitle"
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.48, ease: [0.16, 1, 0.3, 1] }}
          className="font-inter text-base sm:text-lg text-zinc-500 max-w-xl leading-relaxed mb-12"
        >
          {t.hero.subtitle}
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.58, ease: [0.16, 1, 0.3, 1] }}
          className="flex items-center gap-4 flex-wrap justify-center"
        >
          <motion.button
            data-testid="hero-cta-shop"
            onClick={scrollToShop}
            whileHover={{ scale: 1.06, boxShadow: '0 0 50px rgba(255,255,255,0.28)' }}
            whileTap={{ scale: 0.97 }}
            className="relative px-10 py-4 bg-white text-black text-sm font-semibold font-inter rounded-full overflow-hidden group"
          >
            <span className="relative z-10">{t.hero.cta1}</span>
            <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/40 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 pointer-events-none" />
          </motion.button>
          <motion.button
            data-testid="hero-cta-reviews"
            onClick={scrollToReviews}
            whileHover={{ scale: 1.06, borderColor: 'rgba(255,255,255,0.4)' }}
            whileTap={{ scale: 0.97 }}
            className="px-10 py-4 bg-white/[0.05] text-white text-sm font-medium font-inter rounded-full border border-white/18 backdrop-blur-xl hover:bg-white/[0.09] transition-all duration-300"
          >
            {t.hero.cta2}
          </motion.button>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2"
      >
        <motion.div animate={{ y: [0, 8, 0], opacity: [0.2, 0.5, 0.2] }} transition={{ duration: 2.5, repeat: Infinity }}>
          <ArrowDown size={15} className="text-zinc-700" />
        </motion.div>
      </motion.div>
    </section>
  );
}
